import { useState, useEffect } from 'react';
import { 
  Box, 
  Drawer, 
  AppBar, 
  Toolbar, 
  Typography, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  IconButton,
  Avatar,
  Divider,
  ListItemButton,
  CssBaseline,
  Tooltip,
  useTheme,
  useMediaQuery,
  Button,
  Menu,
  MenuItem,
  Collapse
} from '@mui/material';
import {
  Menu as MenuIcon,
  PostAdd as PostIcon,
  CalendarMonth as CalendarIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Collections as PostsIcon,
  BarChart as AnalyticsIcon,
  Image as ImageIcon,
  Star as StarIcon,
  Topic as TopicIcon,
  LinkedIn as LinkedInIcon,
  TrendingUp as TrendingUpIcon,
  Brush as BrushIcon,
  Description as DescriptionIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Dashboard as DashboardIcon,
  Template as TemplateIcon,
  Add as AddIcon,
  Collections as CollectionsIcon,
  Help as HelpIcon,
  Message as MessageIcon,
  People as PeopleIcon,
  Comment as CommentIcon,
  ViewCarousel as ViewCarouselIcon,
  DesignServices as DesignServicesIcon,
  Web as WebIcon,
  PhotoFilter as PhotoFilterIcon,
  Movie as MovieIcon
} from '@mui/icons-material';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useCredits } from '../../hooks/useCredits';
import { useLayoutContext } from '../../context/LayoutContext';
import React from 'react';
import TrendFinderPage from '../../pages/TrendFinder';
import CreditAnimation from '../common/CreditAnimation';
import instationLogo from './Instation-complete.svg';
import { eventService } from '../../services/eventService';

const DashboardLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user, logout } = useAuth();
  const { credits, refetchCredits } = useCredits();
  const { isExpanded, toggleSidebar, collapseSidebar } = useLayoutContext();
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
  const [showAnimation, setShowAnimation] = useState(false);
  const prevCredits = React.useRef(null);

  // Create ref outside of useEffect
  const bannerDesignerVisited = React.useRef(false);

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/payments/subscription`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        setSubscription(response.data.subscription);
      } catch (err) {
        console.error('Error fetching subscription:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, []);

  useEffect(() => {
    if (!loading && credits !== null) {
      if (prevCredits.current !== null && credits < prevCredits.current) {
        setShowAnimation(true);
        setTimeout(() => setShowAnimation(false), 1000);
      }
      prevCredits.current = credits;
    }
  }, [credits, loading]);

  useEffect(() => {
    const unsubscribe = eventService.subscribe('CREDITS_UPDATED', () => {
      refetchCredits();
    });
    
    return () => unsubscribe();
  }, [refetchCredits]);

  useEffect(() => {
    // Redirect to dashboard if at root dashboard path
    if (location.pathname === '/dashboard') {
      navigate('/dashboard/home');
    }
  }, [location.pathname, navigate]);

  // Auto-collapse sidebar when Banner Designer is opened
  useEffect(() => {
    if (location.pathname === '/dashboard/banner-designer' && isExpanded) {
      // Only auto-collapse if this is the first visit to the page
      if (!bannerDesignerVisited.current) {
        collapseSidebar();
        bannerDesignerVisited.current = true;
      }
    } else if (location.pathname !== '/dashboard/banner-designer') {
      // Reset the flag when navigating away from Banner Designer
      bannerDesignerVisited.current = false;
    }
  }, [location.pathname, isExpanded, collapseSidebar]);

  const isActiveRoute = (path) => {
    // For the dashboard home route
    if (path === '/dashboard/generate-post' && location.pathname === '/dashboard') {
      return true;
    }
    return location.pathname === path;
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchorEl(event.currentTarget);
  };
  
  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const drawerWidth = isExpanded ? 280 : 72;

  const menuCategories = [
    {
      category: 'Overview',
      items: [
        {
          title: 'Dashboard',
          path: '/dashboard/home',
          icon: <DashboardIcon sx={{ color: isActiveRoute('/dashboard/home') ? '#4890fe' : 'inherit' }} />
        }
      ]
    },
    {
      category: 'Content Creation',
      items: [
        {
          title: 'Generate Post',
          path: '/dashboard/generate-post',
          icon: <PostIcon sx={{ color: isActiveRoute('/dashboard/generate-post') ? '#4890fe' : 'inherit' }} />
        },
        {
          title: 'Generate Image',
          path: '/dashboard/generate-image', 
          icon: <ImageIcon sx={{ color: isActiveRoute('/dashboard/generate-image') ? '#4890fe' : 'inherit' }} />
        }
      ]
    },
    {
      category: 'Content Planning',
      items: [
        {
          title: 'Topic Generator',
          path: '/dashboard/topic-generator',
          icon: <TopicIcon sx={{ color: isActiveRoute('/dashboard/topic-generator') ? '#4890fe' : 'inherit' }} />
        },
        {
          title: 'Trends & News Finder',
          path: '/dashboard/trend-finder',
          icon: <TrendingUpIcon sx={{ color: isActiveRoute('/dashboard/trend-finder') ? '#4890fe' : 'inherit' }} />
        }
      ]
    },
    {
      category: 'Design Tools',
      items: [
        {
          title: 'GIF Creator',
          path: '/dashboard/gif-creator',
          icon: <MovieIcon sx={{ color: isActiveRoute('/dashboard/gif-creator') ? '#4890fe' : 'inherit' }} />
        },
        {
          title: 'Carousel Generator',
          path: '/dashboard/generate-carousel',
          icon: <ViewCarouselIcon sx={{ color: isActiveRoute('/dashboard/generate-carousel') ? '#4890fe' : 'inherit' }} />
        },
        {
          title: 'Carousel Designer',
          path: '/carousel-templates',
          icon: <DesignServicesIcon sx={{ color: isActiveRoute('/carousel-templates') ? '#4890fe' : 'inherit' }} />
        },
        {
          title: 'Banner Designer',
          path: '/banner-templates',
          icon: <WebIcon sx={{ color: isActiveRoute('/banner-templates') ? '#4890fe' : 'inherit' }} />
        },
        {
          title: 'Image Designer',
          path: '/image-templates',
          icon: <PhotoFilterIcon sx={{ color: isActiveRoute('/image-templates') ? '#4890fe' : 'inherit' }} />
        }
      ]
    },
    {
      category: 'Content Inspiration',
      items: [
        {
          title: 'Viral LinkedIn Posts',
          path: '/dashboard/viral-posts',
          icon: <LinkedInIcon sx={{ color: isActiveRoute('/dashboard/viral-posts') ? '#4890fe' : 'inherit' }} />
        },
        {
          title: 'Trending Influencers',
          path: '/dashboard/influencers',
          icon: <PeopleIcon sx={{ color: isActiveRoute('/dashboard/influencers') ? '#4890fe' : 'inherit' }} />
        }
      ]
    },
    {
      category: 'Profile Optimization',
      items: [
        {
          title: 'About Section',
          path: '/dashboard/about-section',
          icon: <DescriptionIcon sx={{ color: isActiveRoute('/dashboard/about-section') ? '#4890fe' : 'inherit' }} />
        }
      ]
    },
    {
      category: 'Analytics & Insights',
      items: [
        {
          title: 'Instation Analytics',
          path: '/dashboard/analytics',
          icon: <AnalyticsIcon sx={{ color: isActiveRoute('/dashboard/analytics') ? '#4890fe' : 'inherit' }} />
        }
      ]
    },
    {
      category: 'Content Management',
      items: [
        {
          title: 'Posts',
          path: '/dashboard/posts',
          icon: <PostsIcon sx={{ color: isActiveRoute('/dashboard/posts') ? '#4890fe' : 'inherit' }} />
        },
        {
          title: 'Calendar',
          path: '/dashboard/calendar',
          icon: <CalendarIcon sx={{ color: isActiveRoute('/dashboard/calendar') ? '#4890fe' : 'inherit' }} />
        }
      ]
    },
    // {
    //   category: 'Engagement Tools',
    //   items: [
    //     {
    //       title: 'Custom LinkedIn Feed',
    //       path: '/dashboard/engagement-profiles',
    //       icon: <PeopleIcon sx={{ color: isActiveRoute('/dashboard/engagement-profiles') ? '#4890fe' : 'inherit' }} />
    //     }
    //   ]
    // },
    {
      category: 'Account',
      items: [
        {
          title: 'Profile',
          path: '/dashboard/profile',
          icon: <PersonIcon sx={{ color: isActiveRoute('/dashboard/profile') ? '#4890fe' : 'inherit' }} />
        },
        {
          title: 'Upgrade',
          path: '/dashboard/upgrade',
          icon: <StarIcon sx={{ color: isActiveRoute('/dashboard/upgrade') ? '#4890fe' : 'inherit' }} />
        }
      ]
    }
  ];

  const navItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Templates', icon: <WebIcon />, path: '/banner-templates' },
    { text: 'My Banners', icon: <ImageIcon />, path: '/my-banners' },
    { text: 'Create New', icon: <AddIcon />, path: '/banner-designer' },
    { text: 'Image Templates', icon: <PhotoFilterIcon />, path: '/image-templates' },
    { text: 'Carousel Templates', icon: <DesignServicesIcon />, path: '/carousel-templates' },
    // ... other items ...
  ];

  const drawer = (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      bgcolor: 'rgba(72, 144, 254, 0.04)',
      color: '#2D3748',
      position: 'relative'
    }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'row',
        alignItems: 'center', 
        justifyContent: 'center',
        p: isExpanded ? 2 : 1.5,
        minHeight: '70px',
        borderBottom: '1px solid rgba(72, 144, 254, 0.2)',
        bgcolor: 'rgba(72, 144, 254, 0.06)'
      }}>
        {isExpanded ? (
          <Link 
            to="https://instation.app/" 
            style={{ 
              textDecoration: 'none', 
              color: 'inherit',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              gap: 2
            }}
          >
            <img 
              src={instationLogo}
              alt="Instation" 
              style={{ 
                height: '42px',
                width: 'auto',
                maxWidth: '200px',
                display: 'block'
              }} 
            />
          </Link>
        ) : (
          <Link 
            to="https://instation.app/" 
            style={{ 
              textDecoration: 'none', 
              color: 'inherit',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%'
            }}
          >
            <img 
              src={instationLogo}
              alt="Instation" 
              style={{ 
                height: '36px',
                width: '36px',
                objectFit: 'cover',
                objectPosition: 'left',
                display: 'block'
              }} 
            />
          </Link>
        )}
      </Box>
      
      <Box sx={{
        flex: 1, 
        overflowY: 'auto',
        pt: 2,
        // Hide scrollbar for Chrome, Safari and Opera
        '&::-webkit-scrollbar': {
          width: '0px', // Set to 0px to completely hide by default
          backgroundColor: 'transparent',
        },
        // Style scrollbar track
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        // Style scrollbar thumb
        '&::-webkit-scrollbar-thumb': {
          background: 'transparent', // Use transparent to hide completely
          borderRadius: '4px',
          border: 'none',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#a1a1a1',
        },
        // Hide scrollbar for Firefox
        scrollbarWidth: 'none',
        // Show scrollbar only on hover (combined styles for webkit and Firefox)
        '&:hover': {
          scrollbarWidth: 'thin',
          scrollbarColor: '#c1c1c1 transparent',
          '&::-webkit-scrollbar': {
            width: '4px', // Show with width when hovering
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1', // Only show thumb on hover
          }
        }
      }}>
        {menuCategories.map((category, index) => (
          <Box key={category.category}>
            {isExpanded && (
              <Typography 
                variant="overline" 
                sx={{ 
                  color: '#718096',
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                  fontSize: '0.65rem',
                  fontFamily: 'var(--font-family)',
                  px: 3,
                  pt: index === 0 ? 1 : 2,
                  pb: 0.5
                }}
              >
                {category.category}
              </Typography>
            )}
            <List disablePadding>
              {category.items.map((item) => (
                <ListItem 
                  key={item.path} 
                  disablePadding 
                  sx={{ 
                    display: 'block',
                    mb: 0.25
                  }}
                >
                  <ListItemButton
                    onClick={() => navigate(item.path)}
                    sx={{
                      minHeight: 40,
                      justifyContent: isExpanded ? 'initial' : 'center',
                      px: 2.5,
                      bgcolor: isActiveRoute(item.path) ? 'rgba(72, 144, 254, 0.12)' : 'transparent',
                      mx: isExpanded ? 1 : 0.5,
                      borderRadius: '8px',
                      '&:hover': {
                        bgcolor: isActiveRoute(item.path) ? 'rgba(72, 144, 254, 0.18)' : 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  >
                    <Tooltip title={!isExpanded ? item.title : ""} placement="right">
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: isExpanded ? 2.5 : 'auto',
                          justifyContent: 'center',
                          color: isActiveRoute(item.path) ? '#4890fe' : '#718096',
                          '& .MuiSvgIcon-root': {
                            fontSize: '1.3rem'
                          }
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                    </Tooltip>
                    {isExpanded && (
                      <ListItemText 
                        primary={item.title} 
                        sx={{ 
                          opacity: isExpanded ? 1 : 0,
                          '& .MuiTypography-root': {
                            fontWeight: isActiveRoute(item.path) ? 600 : 500,
                            color: isActiveRoute(item.path) ? '#2D3748' : '#4A5568',
                            fontSize: '0.85rem',
                            fontFamily: 'var(--font-family)',
                            lineHeight: 1.2
                          }
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        ))}
      </Box>
      
      <Box sx={{ 
        p: 2, 
        pt: 2,
        borderTop: '1px solid rgba(72, 144, 254, 0.2)',
        mt: 'auto'
      }}>
        {isExpanded && user && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Credits */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box sx={{ position: 'relative', flex: 1 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  size="small"
                  sx={{ 
                    color: '#4890fe',
                    borderColor: 'rgba(72, 144, 254, 0.3)',
                    '&:hover': {
                      borderColor: 'rgba(72, 144, 254, 0.5)',
                      backgroundColor: 'rgba(72, 144, 254, 0.05)'
                    },
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    py: 0.5,
                    borderRadius: '4px'
                  }}
                  disabled={loading}
                >
                  Credits: {loading ? '...' : (credits === null ? '...' : (credits > 999 ? '∞' : credits))}
                </Button>
                <CreditAnimation 
                  show={showAnimation} 
                  type="credit" 
                />
              </Box>
            </Box>

            {/* Status badge */}
            <Box 
              component="button"
              onClick={(e) => handleUserMenuOpen(e)}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                px: 2,
                py: 0.75,
                borderRadius: 2,
                bgcolor: 'rgba(72, 144, 254, 0.05)',
                border: '1px solid rgba(72, 144, 254, 0.1)',
                cursor: 'pointer',
                width: '100%',
                '&:hover': { bgcolor: 'rgba(72, 144, 254, 0.08)' },
                transition: 'background-color 0.2s ease'
              }}>
              <Avatar 
                alt={user.name || 'User'} 
                src={user.picture}
                sx={{ width: 24, height: 24 }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: subscription?.status === 'active' ? '#68D391' : '#CBD5E0'
                  }}
                />
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#4A5568', fontSize: '0.8rem' }}>
                  {subscription?.status === 'active' ? 
                    (subscription.price_id === 'pri_01jwp086ffhm7zb40jzx3vpf1d' ? 'Business' : 'Pro') : 
                    'Free'}
                </Typography>
                <ChevronRightIcon sx={{ fontSize: 18, color: '#718096' }} />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );

  // Scroll to top when route changes
  useEffect(() => {
    const mainContent = document.getElementById('dashboard-content-wrapper');
    if (mainContent) {
      mainContent.scrollTop = 0;
    }
    window.scrollTo(0, 0);
  }, [location.pathname]);

  console.log('Current path:', window.location.pathname);
  console.log('Rendering children:', children);

  return (
    <Box 
      className="dashboard-layout"
      sx={{ 
        display: 'flex',
        minHeight: '100vh',
        bgcolor: '#f8fafc'
      }}
    >
      <CssBaseline />
      
      <Box
        component="nav"
        className="dashboard-nav"
        sx={{
          width: { sm: drawerWidth },
          flexShrink: { sm: 0 },
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100vh',
          zIndex: 100
        }}
      >
        <IconButton 
          onClick={toggleSidebar} 
          sx={{ 
            position: 'fixed',
            top: '85px',
            left: isExpanded ? '266px' : '58px',
            zIndex: 1400,
            color: 'rgba(72, 144, 254, 0.7)',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(72, 144, 254, 0.15)',
            borderRadius: '50%',
            padding: 0,
            width: '30px',
            height: '30px',
            minWidth: '30px',
            minHeight: '30px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: theme => theme.transitions.create('left', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            '& .MuiSvgIcon-root': {
              fontSize: '16px',
            },
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 1)',
              color: 'rgba(72, 144, 254, 0.9)',
            }
          }}
        >
          {isExpanded ? 
            <ChevronLeftIcon /> : 
            <ChevronRightIcon />
          }
        </IconButton>
        
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              height: '100%',
              transition: theme => theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              height: '100%',
              transition: theme => theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              overflowX: 'hidden',
              borderRight: '1px solid rgba(0, 0, 0, 0.06)',
              boxShadow: '2px 0 10px rgba(0, 0, 0, 0.03)'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        className="dashboard-main"
        sx={{
          flexGrow: 1,
          pl: { sm: `${drawerWidth}px` },
          pt: 0,
          transition: theme => theme.transitions.create(['padding', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Box 
          className="dashboard-content-wrapper"
          id="dashboard-content-wrapper"
          sx={{
            p: { xs: 2, sm: 3 },
            pt: { xs: 4, sm: 5 },
            height: '100%',
          }}
        >
          <Box 
            className="dashboard-content"
            sx={{
              height: '100%',
              maxWidth: '1600px',
              mx: 'auto',
              mt: 0
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>

      <Menu
        anchorEl={userMenuAnchorEl}
        open={Boolean(userMenuAnchorEl)}
        onClose={handleUserMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 220,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            borderRadius: 2,
            '& .MuiMenuItem-root': {
              py: 1
            }
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => { handleUserMenuClose(); navigate('/dashboard/profile'); }}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText 
            primary="Profile" 
            secondary={user?.email}
            primaryTypographyProps={{
              sx: { fontSize: '0.9rem' }
            }}
            secondaryTypographyProps={{
              sx: { fontSize: '0.8rem' }
            }}
          />
        </MenuItem>
        <MenuItem onClick={() => { handleUserMenuClose(); navigate('/dashboard/upgrade'); }}>
          <ListItemIcon>
            <StarIcon fontSize="small" />
          </ListItemIcon>
          Upgrade Plan
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { handleUserMenuClose(); handleLogout(); }}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" color="error" />
          </ListItemIcon>
          <Typography color="error">Logout</Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default DashboardLayout; 